import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectInstructorId, selectIsAuthenticated } from "../../authentication/authenticationSlice";
import {
  selectActiveContextKey,
  selectContexts,
  selectContextsStatus,
  selectSwitchFailed,
  selectSwitchingTo,
} from "../contexts-slice";
import { Context } from "../models/Context";
import { fetchContexts, switchContext } from "../slice-extensions/contexts-slice-extension";
import { instructorContextKey } from "../utils/contextLabels";

export interface ContextsView {
  contexts: Context[];
  /** The context the server remembers. */
  activeKey: string | null;
  /** The context the stored instructor token speaks for; null without one. */
  currentKey: string | null;
  loaded: boolean;
  switchingTo: string | null;
  switchFailed: boolean;
  switchTo: (context: Context) => void;
}

/** The signed-in instructor's contexts, fetched once per page load, and the switch to one of them. */
export const useContexts = (): ContextsView => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const instructorId = useAppSelector(selectInstructorId);
  const contexts = useAppSelector(selectContexts);
  const activeKey = useAppSelector(selectActiveContextKey);
  const status = useAppSelector(selectContextsStatus);
  const switchingTo = useAppSelector(selectSwitchingTo);
  const switchFailed = useAppSelector(selectSwitchFailed);

  useEffect(() => {
    if (isAuthenticated && status === "idle") dispatch(fetchContexts());
  }, [isAuthenticated, status, dispatch]);

  const switchTo = useCallback((context: Context) => void dispatch(switchContext(context)), [dispatch]);

  return {
    contexts: isAuthenticated ? contexts : [],
    activeKey,
    currentKey: instructorId ? instructorContextKey(instructorId) : null,
    loaded: status === "succeeded" || status === "failed",
    switchingTo,
    switchFailed,
    switchTo,
  };
};
